<template>
    <div class="pt-3">
        <div ref="dropRef" class="dropzone-box p-10">
            <div class="text-center text-gray-500 pointer-events-none">
                <p class="font-bold">Glissez vos images ici</p>
                <p class="font-semibold">ou cliquez pour sélectionner</p>
                <p class="text-sm">JPG / PNG · max {{ MAX_FILES }} images · {{ MAX_FILESIZE_MB }} Mo chacune</p>
            </div>
        </div>

        <p v-if="errorMessage" class="mt-2 text-sm font-semibold text-red-600">
            {{ errorMessage }}
        </p>

        <div class="grid grid-cols-3 gap-3 my-4">
            <div
                v-for="(img, index) in previews"
                :key="img.uuid || img.id || index"
                class="relative aspect-square overflow-hidden rounded-xl group border"
            >
                <img :src="img.original_url" class="w-full h-full object-cover group-hover:scale-105 transition" />

                <button
                    type="button"
                    @click="remove(img, index)"
                    class="absolute top-2 right-2 bg-black/60 hover:bg-red-400 text-white text-xs px-1 py-.5 rounded"
                >
                    <i class="fad fa-times-circle text-sm"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, onMounted, watch } from 'vue'
    import Dropzone from 'dropzone'
    import axios from 'axios'

    Dropzone.autoDiscover = false

    export default {
        props: {
            route: {
                type: String,
                required: true,
            },
            media: {
                type: Array,
                default: () => [],
            },
        },

        setup(props) {
            const dropRef = ref(null)
            const previews = ref([...(props.media || [])])
            const errorMessage = ref('')

            const MAX_FILES = 6
            const MAX_FILESIZE_MB = 10

            watch(
                () => props.media,
                (value) => {
                    previews.value = [...(value || [])]
                }
            )

            const extractErrorMessage = (error) => {
                if (!error) {
                    return 'Impossible d’envoyer l’image.'
                }

                if (typeof error === 'string') {
                    return error
                }

                if (error.errors) {
                    const first = Object.values(error.errors)[0]
                    return Array.isArray(first) ? first[0] : String(first)
                }

                if (error.message) {
                    return error.message
                }

                return 'Impossible d’envoyer l’image.'
            }

            onMounted(() => {
                if (!dropRef.value || !props.route) {
                    errorMessage.value = 'URL d’upload invalide.'
                    return
                }

                const dz = new Dropzone(dropRef.value, {
                    url: props.route,
                    method: 'POST',
                    paramName: 'files',
                    uploadMultiple: true,
                    parallelUploads: MAX_FILES,
                    maxFiles: MAX_FILES,
                    maxFilesize: MAX_FILESIZE_MB,
                    acceptedFiles: 'image/jpeg,image/png,.jpg,.jpeg,.png',
                    previewTemplate: '<div style="display:none"></div>',
                    previewsContainer: false,
                    clickable: dropRef.value,
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Accept: 'application/json',
                    },
                    dictDefaultMessage: '',
                    dictFileTooBig: `Fichier trop volumineux (max ${MAX_FILESIZE_MB} Mo).`,
                    dictInvalidFileType: 'Format non supporté. Utilisez JPG ou PNG.',
                    dictMaxFilesExceeded: `Maximum ${MAX_FILES} images autorisées.`,
                    init() {
                        const dzInstance = this

                        this.on('addedfile', (file) => {
                            errorMessage.value = ''

                            if (previews.value.length >= MAX_FILES) {
                                dzInstance.removeFile(file)
                                errorMessage.value = `Maximum ${MAX_FILES} images autorisées.`
                            }
                        })

                        this.on('successmultiple', (files, response) => {
                            errorMessage.value = ''
                            const newImages = response.media || []
                            previews.value.push(...newImages)

                            if (previews.value.length > MAX_FILES) {
                                previews.value = previews.value.slice(0, MAX_FILES)
                            }
                        })

                        this.on('error', (file, error) => {
                            dzInstance.removeFile(file)
                            errorMessage.value = extractErrorMessage(error)
                            console.error('Upload image colis:', error)
                        })
                    },
                })
            })

            const remove = async (img, index) => {
                errorMessage.value = ''

                try {
                    if (img.uuid) {
                        await axios.delete(route('media.destroy', img.uuid))
                    }
                    previews.value.splice(index, 1)
                } catch (error) {
                    errorMessage.value = 'Impossible de supprimer l’image.'
                    console.error(error)
                }
            }

            return {
                dropRef,
                previews,
                remove,
                errorMessage,
                MAX_FILES,
                MAX_FILESIZE_MB,
            }
        },
    }
</script>

<style scoped>
    .dropzone-box {
        border: 2px dashed #0ea5e9;
        padding: 30px;
        border-radius: 12px;
        background: #f8fafc;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .dropzone-box:hover {
        background: #f1f5f9;
        border-color: #0284c7;
    }

    .pointer-events-none {
        pointer-events: none;
    }
</style>
